import React, { useEffect, useState } from "react";
import { X, Plus } from "lucide-react";
import { toast } from "sonner";

import { PanelInput } from "@/components/panels/Drawer";

const TIMEZONES = [
  "Europe/London", "Europe/Berlin", "Europe/Paris", "Europe/Madrid", "Europe/Amsterdam",
  "America/New_York", "America/Los_Angeles", "America/Chicago", "America/Denver",
  "Australia/Sydney", "Asia/Tokyo", "Asia/Singapore", "UTC",
];
const TIMEZONE_OPTIONS = TIMEZONES.map((tz) => ({ value: tz, label: tz }));

const EMPTY = { name: "", timezone: "Europe/London", start_date: "", end_date: "", notes: "" };

/**
 * Centered modal for blank-project creation. Console-styled to match the rest.
 * Submits POST /api/projects then asks the parent to switch into the new project.
 */
export default function NewProjectDialog({ open, onClose, onCreate }) {
  const [draft, setDraft] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setDraft(EMPTY);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async () => {
    const name = draft.name.trim();
    if (!name) return;
    setBusy(true);
    try {
      await onCreate({
        name,
        timezone: draft.timezone,
        start_date: draft.start_date || null,
        end_date: draft.end_date || null,
        notes: draft.notes || null,
      });
      toast.success(`Project "${name}" created`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create project.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="new-project-overlay"
    >
      <div
        className="w-[440px] max-w-[92vw] bg-bg-surface border border-line-strong shadow-[0_24px_64px_rgba(0,0,0,0.8)] animate-slide-up"
        onClick={(e) => e.stopPropagation()}
        data-testid="new-project-dialog"
      >
        <div className="flex items-center justify-between p-3 border-b border-line-strong bg-bg-elevated">
          <div>
            <div className="mono text-2xs uppercase tracking-[0.2em] text-ink-mute">NEW PROJECT</div>
            <div className="text-sm text-ink mt-0.5">Start a blank console</div>
          </div>
          <button onClick={onClose} className="btn-ghost" data-testid="new-project-close">
            <X className="w-3 h-3" />
          </button>
        </div>

        <div className="p-4 space-y-1">
          <PanelInput
            label="NAME *"
            value={draft.name}
            onChange={(v) => setDraft({ ...draft, name: v })}
            placeholder="Glastonbury 2026"
            dataTestid="new-project-name"
          />
          <PanelInput
            label="TIMEZONE"
            value={draft.timezone}
            onChange={(v) => setDraft({ ...draft, timezone: v })}
            options={TIMEZONE_OPTIONS}
            dataTestid="new-project-timezone"
          />
          <div className="grid grid-cols-2 gap-2">
            <PanelInput
              label="START DATE"
              type="date"
              value={draft.start_date}
              onChange={(v) => setDraft({ ...draft, start_date: v })}
              dataTestid="new-project-start"
            />
            <PanelInput
              label="END DATE"
              type="date"
              value={draft.end_date}
              onChange={(v) => setDraft({ ...draft, end_date: v })}
              dataTestid="new-project-end"
            />
          </div>
          <PanelInput
            label="NOTES"
            value={draft.notes}
            onChange={(v) => setDraft({ ...draft, notes: v })}
            placeholder="optional"
            dataTestid="new-project-notes"
          />
          <p className="mono text-2xs text-ink-mute uppercase tracking-wider mt-2">
            Blank consoles start with no rooms, crew, tasks or equipment. Add them from the side panels once the project opens.
          </p>
        </div>

        <div className="p-3 border-t border-line bg-bg-surface flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn-ghost" data-testid="new-project-cancel">
            CANCEL
          </button>
          <button
            onClick={submit}
            disabled={busy || !draft.name.trim()}
            className="btn-primary px-4"
            data-testid="new-project-submit"
          >
            <Plus className="w-3 h-3" /> {busy ? "Creating…" : "Create project"}
          </button>
        </div>
      </div>
    </div>
  );
}
